import documentsService from "../services/documents";

/**
 * ============================================================================
 * Templates API
 * Pre-built specialty form templates (read-only)
 * ============================================================================
 */

/**
 * List templates with optional category filter and text search
 * @param {Object} params - Query parameters
 * @param {string} params.category - Filter by category (e.g., "General Medicine")
 * @param {string} params.search - Search in name/description
 * @param {number} params.limit - Results per page (1-100, default: 20)
 * @param {number} params.offset - Skip N results (default: 0)
 * @returns {Promise} API response with templates array
 */
export function getTemplatesApi(params = {}) {
  return documentsService({
    url: "/templates",
    method: "get",
    params
  });
}

/**
 * Get a single template with its full definition
 * @param {string} id - Template ID
 * @returns {Promise} API response with template details
 */
export function getTemplateByIdApi(id) {
  return documentsService({
    url: `/templates/${id}`,
    method: "get"
  });
}

/**
 * ============================================================================
 * Forms API
 * User-created forms with full lifecycle management
 * ============================================================================
 */

/**
 * Create a new form. Optionally clone from a template.
 * @param {Object} data - Form data
 * @param {string} data.title - Form title (required)
 * @param {string} data.description - Form description
 * @param {string} data.category - Category label
 * @param {string} data.emoji_tag - Emoji tag for UI display
 * @param {string} data.template_id - Clone definition from this template
 * @param {Object} data.definition - Form definition JSON { fields: [...] }
 * @returns {Promise} API response with created form
 */
export function createFormApi(data) {
  return documentsService({
    url: "/forms",
    method: "post",
    data
  });
}

/**
 * List forms for the current user's clinic
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status: DRAFT, PUBLISHED, ARCHIVED
 * @param {number} params.limit - Results per page (1-100, default: 20)
 * @param {number} params.offset - Skip N results (default: 0)
 * @returns {Promise} API response with forms array
 */
export function getFormsApi(params = {}) {
  return documentsService({
    url: "/forms",
    method: "get",
    params
  });
}

/**
 * Get form detail including full definition and mapping status
 * @param {string} id - Form ID
 * @returns {Promise} API response with form details
 */
export function getFormByIdApi(id) {
  return documentsService({
    url: `/forms/${id}`,
    method: "get"
  });
}

/**
 * Update a form (draft only). Automatically creates a FormVersion snapshot.
 * @param {string} id - Form ID
 * @param {Object} data - Update data
 * @param {string} data.title - Updated title
 * @param {string} data.description - Updated description
 * @param {string} data.category - Updated category
 * @param {string} data.emoji_tag - Updated emoji tag
 * @param {Object} data.definition - Updated form definition
 * @param {string} data.change_summary - Version change note
 * @returns {Promise} API response with updated form
 */
export function updateFormApi(id, data) {
  return documentsService({
    url: `/forms/${id}`,
    method: "put",
    data
  });
}

/**
 * Clone an existing form into a new DRAFT form
 * @param {string} id - Form ID to duplicate
 * @returns {Promise} API response with new form
 */
export function duplicateFormApi(id) {
  return documentsService({
    url: `/forms/${id}/duplicate`,
    method: "post"
  });
}

/**
 * Soft-delete a form by setting its status to ARCHIVED
 * @param {string} id - Form ID
 * @returns {Promise} API response with archived form
 */
export function deleteFormApi(id) {
  return documentsService({
    url: `/forms/${id}`,
    method: "delete"
  });
}

/**
 * Transition form from DRAFT to PUBLISHED
 * @param {string} id - Form ID
 * @returns {Promise} API response with published form
 */
export function publishFormApi(id) {
  return documentsService({
    url: `/forms/${id}/publish`,
    method: "put"
  });
}

/**
 * Archive a form
 * @param {string} id - Form ID
 * @returns {Promise} API response with archived form
 */
export function archiveFormApi(id) {
  return documentsService({
    url: `/forms/${id}/archive`,
    method: "put"
  });
}

/**
 * Transition form from PUBLISHED back to DRAFT
 * @param {string} id - Form ID
 * @returns {Promise} API response with unpublished form
 */
export function unpublishFormApi(id) {
  return documentsService({
    url: `/forms/${id}/unpublish`,
    method: "put"
  });
}

/**
 * Send a form to a patient
 * @param {string} formId - Form ID
 * @param {Object} data - Send data
 * @param {number} data.patient_id - Patient ID (required)
 * @returns {Promise} API response with form assignment details
 */
export function sendFormApi(formId, data) {
  return documentsService({
    url: `/forms/${formId}/send`,
    method: "post",
    data
  });
}

/**
 * Get the submission status of a form assignment
 * @param {string} assignmentId - Assignment ID
 * @returns {Promise} API response with submission status
 */
export function getFormSubmissionStatusApi(assignmentId) {
  return documentsService({
    url: `/assignments/${assignmentId}/submission-status`,
    method: "get",
  });
}

/**
 * ============================================================================
 * Custom Elements API
 * Reusable form element bundles scoped to a clinic
 * ============================================================================
 */

/**
 * Create a custom element bundle
 * @param {Object} data - Custom element data
 * @param {string} data.name - Element name (required)
 * @param {string} data.emoji - Emoji icon
 * @param {Object} data.definition - Element definition JSON { fields: [...] } (required)
 * @returns {Promise} API response with created custom element
 */
export function createCustomElementApi(data) {
  return documentsService({
    url: "/custom-elements",
    method: "post",
    data
  });
}

/**
 * List custom elements for the current user's clinic
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Results per page (1-100, default: 20)
 * @param {number} params.offset - Skip N results (default: 0)
 * @returns {Promise} API response with custom elements array
 */
export function getCustomElementsApi(params = {}) {
  return documentsService({
    url: "/custom-elements",
    method: "get",
    params
  });
}

/**
 * Get a single custom element with its full definition
 * @param {string} id - Custom element ID
 * @returns {Promise} API response with custom element details
 */
export function getCustomElementByIdApi(id) {
  return documentsService({
    url: `/custom-elements/${id}`,
    method: "get"
  });
}

/**
 * Update a custom element. Does NOT affect forms already using the element.
 * @param {string} id - Custom element ID
 * @param {Object} data - Update data
 * @param {string} data.name - Element name
 * @param {string} data.emoji - Emoji icon
 * @param {Object} data.definition - Element definition JSON
 * @returns {Promise} API response with updated custom element
 */
export function updateCustomElementApi(id, data) {
  return documentsService({
    url: `/custom-elements/${id}`,
    method: "put",
    data
  });
}

/**
 * Delete a custom element. Does NOT affect forms already using the element.
 * @param {string} id - Custom element ID
 * @returns {Promise} API response
 */
export function deleteCustomElementApi(id) {
  return documentsService({
    url: `/custom-elements/${id}`,
    method: "delete"
  });
}

/**
 * ============================================================================
 * AI Assistant API
 * AI-powered form field generation and EHR mapping suggestions
 * ============================================================================
 */

/**
 * Generate form fields from a natural language description
 * @param {Object} data - Generation request
 * @param {string} data.purpose - Purpose of the form (required)
 * @param {string} data.description - Additional context or requirements
 * @param {Object} data.existing_fields - Existing fields to preserve/extend
 * @returns {Promise} API response with generated fields
 */
export function generateFieldsApi(data) {
  return documentsService({
    url: "/ai/generate-fields",
    method: "post",
    data
  });
}

/**
 * Suggest EHR field mappings for form fields
 * @param {Object} data - Mapping request
 * @param {string} data.form_id - Form ID (required)
 * @param {Object} data.fields - Fields to suggest mappings for (required)
 * @returns {Promise} API response with suggested mappings
 */
export function suggestMappingsApi(data) {
  return documentsService({
    url: "/ai/suggest-mappings",
    method: "post",
    data
  });
}

/**
 * ============================================================================
 * Submissions API
 * Patient form submissions
 * ============================================================================
 */

/**
 * Submit a completed form
 * @param {string} formId - Form ID
 * @param {Object} data - Submission data
 * @param {string} data.patient_id - Patient ID (required)
 * @param {Object} data.payload - Submitted field values { fieldId: value } (required)
 * @returns {Promise} API response with created submission
 */
export function createSubmissionApi(formId, data) {
  return documentsService({
    url: `/forms/${formId}/submissions`,
    method: "post",
    data
  });
}

/**
 * List submissions for a specific form
 * @param {string} formId - Form ID
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Results per page (1-100, default: 20)
 * @param {number} params.offset - Skip N results (default: 0)
 * @returns {Promise} API response with submissions array
 */
export function getFormSubmissionsApi(formId, params = {}) {
  return documentsService({
    url: `/forms/${formId}/submissions`,
    method: "get",
    params
  });
}

/**
 * Get a single submission with its payload and resolved EHR mappings
 * @param {string} id - Submission ID
 * @returns {Promise} API response with submission details
 */
export function getSubmissionByIdApi(id) {
  return documentsService({
    url: `/submissions/${id}`,
    method: "get"
  });
}

/**
 * ============================================================================
 * File Storage API
 * File upload/download with client-side encryption
 * ============================================================================
 */

/**
 * Upload a file. Max size: 10 MB.
 * @param {FormData} formData - Form data with file
 * @returns {Promise} API response with file metadata
 */
export function uploadFileApi(formData) {
  return documentsService({
    url: "/files/upload",
    method: "post",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
}

/**
 * Get file metadata (does not return the file content)
 * @param {string} id - File ID
 * @returns {Promise} API response with file metadata
 */
export function getFileMetadataApi(id) {
  return documentsService({
    url: `/files/${id}`,
    method: "get"
  });
}

/**
 * Download the decrypted file
 * @param {string} id - File ID
 * @returns {Promise} API response with binary file data
 */
export function downloadFileApi(id) {
  return documentsService({
    url: `/files/${id}/download`,
    method: "get",
    responseType: "blob"
  });
}

/**
 * Delete a file from both R2 storage and the database
 * @param {string} id - File ID
 * @returns {Promise} API response
 */
export function deleteFileApi(id) {
  return documentsService({
    url: `/files/${id}`,
    method: "delete"
  });
}

/**
 * ============================================================================
 * Registry API
 * Service-to-service APIs for external domain services
 * ============================================================================
 */

/**
 * List all published forms
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Results per page (1-100, default: 20)
 * @param {number} params.offset - Skip N results (default: 0)
 * @returns {Promise} API response with published forms array
 */
export function getRegistryFormsApi(params = {}) {
  return documentsService({
    url: "/registry/forms",
    method: "get",
    params
  });
}

/**
 * Get full form definition including field mappings
 * @param {string} id - Form ID
 * @returns {Promise} API response with form definition
 */
export function getRegistryFormByIdApi(id) {
  return documentsService({
    url: `/registry/forms/${id}`,
    method: "get"
  });
}

/**
 * Get submission payload with resolved EHR mappings
 * @param {string} id - Submission ID
 * @returns {Promise} API response with submission details
 */
export function getRegistrySubmissionByIdApi(id) {
  return documentsService({
    url: `/registry/submissions/${id}`,
    method: "get"
  });
}

/**
 * ============================================================================
 * Patient Completed Forms API
 * ============================================================================
 */

/**
 * Get completed forms for a patient
 * @param {string} patientId - Patient ID
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Results per page (1-100, default: 20)
 * @param {number} params.offset - Skip N results (default: 0)
 * @returns {Promise} API response with assignments array
 */
export function getPatientCompletedFormsApi(patientId, params = {}) {
  return documentsService({
    url: `/patients/${patientId}/completed-forms`,
    method: "get",
    params
  });
}

/**
 * ============================================================================
 * Health Check
 * ============================================================================
 */

/**
 * Health check endpoint (no authentication required)
 * @returns {Promise} API response with health status
 */
export function getHealthApi() {
  return documentsService({
    url: "/health",
    method: "get"
  });
}
